import { Currency } from "@paywithglide/glide-js";

/**
 * Check if a currency is a bridged/wrapped token
 */
export function isBridgedToken(currency: Currency): boolean {
  const name = currency.name.toLowerCase();
  return (
    name.includes("bridged") ||
    name.includes("wrapped") ||
    name.includes("portal") ||
    name.includes("axelar") ||
    name.includes("wormhole") ||
    name.includes("layerzero") ||
    (name.includes("binance") && !currency.symbol.includes("BNB"))
  );
}

/**
 * Score a currency name based on how clean/canonical it is.
 * Higher scores are better.
 * Used to select the best display name when multiple currencies share the same symbol.
 * Does NOT consider tier - only name quality.
 */
export function getCurrencyNameScore(currency: Currency): number {
  const name = currency.name.toLowerCase();
  const symbol = currency.symbol.toLowerCase();
  let score = 0;

  // Heavily penalize bridged/wrapped tokens
  if (isBridgedToken(currency)) score -= 10000;

  // Bonus for exact symbol match (e.g., "USDC" over "USD Coin")
  if (currency.name === currency.symbol) score += 500;

  // Bonus for common canonical names
  if (
    (symbol === "usdc" && name === "usd coin") ||
    (symbol === "usdt" && name === "tether") ||
    (symbol === "eth" && name === "ethereum") ||
    (symbol === "weth" && name === "wrapped ether") ||
    (symbol === "wbtc" && name === "wrapped bitcoin")
  ) {
    score += 400;
  }

  // Prefer shorter names (likely more canonical)
  score -= currency.name.length;

  return score;
}

/**
 * Select the best currency to use as a representative from a group
 * of currencies with the same symbol.
 * Prefers native tokens over bridged/wrapped variants.
 */
export function selectBestCurrencyRepresentative(
  currencies: Currency[],
): Currency {
  if (currencies.length === 0) {
    throw new Error("Cannot select best representative from empty array");
  }

  if (currencies.length === 1) {
    return currencies[0];
  }

  // Sort by name score and return the best one
  const sorted = [...currencies].sort((a, b) => {
    return getCurrencyNameScore(b) - getCurrencyNameScore(a);
  });

  return sorted[0];
}
